import { useEffect, useState } from "react";
import { useGetOfficeApplianceBrand } from "../hooks/useGetOfficeApplianceBrand";
import { useGetOfficeApplianceType } from "../hooks/useGetOfficeApplianceType";
import { useGetRetailStoreOptions } from "../hooks/useGetRetailStoreOptions";
import { useFilteredOfficeAppliance } from "../hooks/useFilteredOfficeAppliance";
import { Disclosure } from "@headlessui/react";
import { BarsArrowUpIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import RefreshButton from "components/RefreshButton";
import SearchButton from "components/SearchButton";
import SelectHookFormMulti from "components/SelectHookFormMulti";
import { useRecoilState } from "recoil";
import { isFilter } from "../atom";

export const OfficeApplianceTableFilter = ({
  tableFilter,
  setFilteredOfficeAppliance,
}) => {
  //#region Options Data
  const { data: officeApplianceType, isLoading: typeLoading } =
    useGetOfficeApplianceType();
  const { data: officeApplianceBrand, isLoading: brandLoading } =
    useGetOfficeApplianceBrand();
  const { data: retailStores, isLoading: storeLoading } =
    useGetRetailStoreOptions();
  //#endregion

  //#region States
  const [, setFilterStatus] = useRecoilState(isFilter);
  const [filterInput, setFilterInput] = useState({
    officeId: null,
    retailStoreNumber: "",
    officeApplianceTypeId: null,
    officeApplianceType: "",
    officeApplianceBrandId: null,
    officeApplianceBrand: "",
  });
  //#endregion

  //#region Filter
  const {
    isSuccess: filterSuccess,
    data: filterResult,
    refetch: filterRefetch,
  } = useFilteredOfficeAppliance({
    filterInput,
  });
  //#endregion

  //#region Filter Success UseEffect
  useEffect(() => {
    if (filterSuccess) {
      setFilterInput((searchInputsData) => ({
        ...searchInputsData,
      }));
      setFilterStatus(true);
      setFilteredOfficeAppliance(filterResult);
    }

    //if search is success
    if (filterSuccess) {
      if (filterResult && !filterResult.statusCode) {
        setFilteredOfficeAppliance(filterResult);
      } else {
        setFilteredOfficeAppliance([]);
      }
    }
  }, [
    filterResult,
    filterSuccess,
    setFilterStatus,
    setFilteredOfficeAppliance,
  ]);
  //#endregion

  //#region Dropdown Options
  const typeOpts = officeApplianceType?.map((x) => {
    return {
      label: x.officeApplianceType1,
      value: x.officeApplianceTypeId,
    };
  });

  const brandOpts = officeApplianceBrand?.map((x) => {
    return {
      label: x.officeApplianceBrand1,
      value: x.officeApplianceBrandId,
    };
  });

  const retailStoreOpts = retailStores?.map((x) => {
    return {
      label: x.retailStoreNumber,
      value: x.officeId,
      address: x.retailStoreAddress,
    };
  });
  //#endregion

  //#region Refresh Table
  const refreshTable = () => {
    setFilterStatus(false);
    setFilteredOfficeAppliance([]);
    setFilterInput({
      ...filterInput,
      officeId: null,
      retailStoreNumber: "",
      officeApplianceTypeId: null,
      officeApplianceType: "",
      officeApplianceBrandId: null,
      officeApplianceBrand: "",
    });
  };
  //#endregion

  return (
    <div className="pt-2 px-4 space-y-2 pl-2">
      <div className="pt-2">
        <BarsArrowUpIcon className="h-6 w-6 text-primary mb-1.5" />
        <div className="text-xl font-bold text-primary -mt-9 ml-8">
          Search Section
        </div>
      </div>
      <div className="w-full">
        <div className="mb-3">
          <Disclosure defaultOpen={tableFilter}>
            {({ open }) => (
              <>
                <Disclosure.Button className="sm:px-2 flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-primary bg-gray-100 rounded-t-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                  <span>Office Appliance Details</span>
                  <ChevronDownIcon
                    className={`${
                      open ? "transform rotate-180" : ""
                    } w-5 h-5 text-gray-500`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="bg-gray-100 text-sm text-primary rounded-b-lg">
                  <div className="bg-gray-100  p-3 sm:px-2 md:px-3">
                    <div className="text-xs text-secondary italic font-normal pb-4">
                      <b> Note:</b> All fields are multiple selection.
                    </div>
                    <div className="flex flex-col text-xs gap-x-4 gap-y-2">
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Retail Store Number
                        </label>
                        <SelectHookFormMulti
                          options={retailStoreOpts}
                          value={filterInput?.retailStoreNumber}
                          onChange={(e) => {
                            setFilterInput({
                              ...filterInput,
                              officeId: e?.map((x) => x.value),
                              retailStoreNumber: e,
                            });
                          }}
                          isLoading={storeLoading}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Brand
                        </label>
                        <SelectHookFormMulti
                          options={brandOpts}
                          value={filterInput?.officeApplianceBrand}
                          onChange={(e) => {
                            setFilterInput({
                              ...filterInput,
                              officeApplianceBrandId: e?.map((x) => x.value),
                              officeApplianceBrand: e,
                            });
                          }}
                          isLoading={brandLoading}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Type
                        </label>
                        <SelectHookFormMulti
                          options={typeOpts}
                          value={filterInput?.officeApplianceType}
                          onChange={(e) => {
                            setFilterInput({
                              ...filterInput,
                              officeApplianceTypeId: e?.map((x) => x.value),
                              officeApplianceType: e,
                            });
                          }}
                          isLoading={typeLoading}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="flex justify-center items-end gap-x-2 mb-12  lg:ml-4 md:ml-1 rounded-md mt-5">
                        <SearchButton
                          buttonValue="Search"
                          onClick={() => {
                            filterRefetch();
                          }}
                        />
                        <RefreshButton
                          onClick={() => {
                            refreshTable();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>
    </div>
  );
};
